import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, RefreshCw, TrendingUp, Percent, DollarSign, Info } from 'lucide-react';
import Swal from 'sweetalert2';

export default function MargeB2BControlPage({ onBack }) {
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/partners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur lors du chargement');

      const data = await response.json();
      setPartners(data.partners);
      setChanges({});
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger les partenaires',
        confirmButtonColor: '#FF7B47'
      });
    } finally {
      setIsLoading(false);
    }
  };

const handleMargeChange = (partnerId, field, value) => {
  const numValue = parseFloat(value);
  
  if (numValue < 0 || numValue > 100) {
    Swal.fire({
      icon: 'warning',
      title: 'Valeur invalide',
      text: 'La marge doit être entre 0% et 100%',
      confirmButtonColor: '#FF7B47'
    });
    return;
  }

  // Only allow changing marge_b2b
  if (field !== 'marge_b2b') return;

  setChanges(prev => ({
    ...prev,
    [partnerId]: {
      ...prev[partnerId],
      marge_b2b: numValue
    }
  }));
};


  const hasChanges = (partnerId) => {
    return changes[partnerId] !== undefined;
  };

  const getDisplayValue = (partner, field) => {
    if (changes[partner.id_partenaire]?.[field] !== undefined) {
      return changes[partner.id_partenaire][field];
    }
    return parseFloat(partner[field]);
  };

const savePartnerChanges = async (partner) => {
  if (!hasChanges(partner.id_partenaire)) return;

  try {
    const token = localStorage.getItem('authToken');
    const updateData = changes[partner.id_partenaire];
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/partners/${partner.id_partenaire}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...partner,
        marge_b2b: updateData.marge_b2b ?? partner.marge_b2b
        // ✅ Don't send marge_partenaire
      })
    });

    if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

    Swal.fire({
      icon: 'success',
      title: 'Succès',
      text: `Marge B2B mise à jour pour ${partner.libelle_partenaire}`,
      timer: 2000,
      showConfirmButton: false
    });

    setChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[partner.id_partenaire];
      return newChanges;
    });

    fetchPartners();

  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: error.message,
      confirmButtonColor: '#FF7B47'
    });
  }
};


const saveAllChanges = async () => {
  const changedPartners = Object.keys(changes);
  
  if (changedPartners.length === 0) {
    Swal.fire({
      icon: 'info',
      title: 'Aucun changement',
      text: 'Aucune modification à enregistrer',
      confirmButtonColor: '#FF7B47'
    });
    return;
  }

  const result = await Swal.fire({
    icon: 'question',
    title: 'Confirmer les modifications',
    text: `Sauvegarder les modifications pour ${changedPartners.length} partenaire(s) ?`,
    showCancelButton: true,
    confirmButtonColor: '#FF7B47',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Oui, sauvegarder',
    cancelButtonText: 'Annuler'
  });

  if (!result.isConfirmed) return;

  setIsSaving(true);

  try {
    const token = localStorage.getItem('authToken');
    
    const promises = changedPartners.map(partnerId => {
      const partner = partners.find(p => p.id_partenaire === parseInt(partnerId));
      const updateData = changes[partnerId];
      
      return fetch(`${process.env.REACT_APP_API_URL}/api/admin/partners/${partnerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...partner,
          marge_b2b: updateData.marge_b2b ?? partner.marge_b2b
          // ✅ REMOVED: marge_partenaire - admin can't modify it
        })
      });
    });

    await Promise.all(promises);

    Swal.fire({
      icon: 'success',
      title: 'Succès',
      text: 'Toutes les marges B2B ont été sauvegardées',
      confirmButtonColor: '#FF7B47'
    });

    setChanges({});
    fetchPartners();

  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: 'Erreur lors de la sauvegarde de certaines modifications',
      confirmButtonColor: '#FF7B47'
    });
  } finally {
    setIsSaving(false);
  }
};


  const resetChanges = () => {
    if (Object.keys(changes).length === 0) return;
    
    Swal.fire({
      icon: 'question',
      title: 'Annuler les modifications',
      text: 'Êtes-vous sûr de vouloir annuler tous les changements non sauvegardés ?',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non'
    }).then((result) => {
      if (result.isConfirmed) {
        setChanges({});
        Swal.fire({
          icon: 'success',
          title: 'Annulé',
          text: 'Tous les changements ont été annulés',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  };

  const changesCount = Object.keys(changes).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-[#2B4979] hover:text-[#FF7B47] font-semibold transition"
            >
              <ArrowLeft size={20} />
              Retour
            </button>
            <h1 className="text-3xl font-bold text-[#2B4979]">Contrôle des Marges B2B</h1>
          </div>

          <div className="flex items-center gap-3">
            {changesCount > 0 && (
              <>
                <button
                  onClick={resetChanges}
                  className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                >
                  <RefreshCw size={20} />
                  Annuler ({changesCount})
                </button>
                <button
                  onClick={saveAllChanges}
                  disabled={isSaving}
                  className="flex items-center gap-2 bg-[#FF7B47] hover:bg-[#ff6a30] text-white px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
                >
                  <Save size={20} />
                  {isSaving ? 'Sauvegarde...' : `Sauvegarder Tout (${changesCount})`}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-bold text-blue-900 mb-1">À propos des marges</h3>
              <p className="text-sm text-blue-800">
                <strong>Marge Partenaire :</strong> Marge appliquée sur les prix pour ce partenaire
                <br />
                <strong>Marge B2B :</strong> Marge supplémentaire pour les clients B2B de ce partenaire
              </p>
            </div>
          </div>
        </div>

{/* Stats Cards */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
  <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#2B4979]">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-semibold">Total Partenaires</p>
        <p className="text-3xl font-bold text-[#2B4979]">{partners.length}</p>
      </div>
      <TrendingUp size={40} className="text-[#2B4979] opacity-20" />
    </div>
  </div>

  <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-semibold">Marge Moyenne B2B</p>
        <p className="text-3xl font-bold text-green-600">
          {partners.length > 0 
            ? (partners.reduce((sum, p) => sum + parseFloat(p.marge_b2b), 0) / partners.length).toFixed(1)
            : 0}%
        </p>
      </div>
      <DollarSign size={40} className="text-green-500 opacity-20" />
    </div>
  </div>
</div>


        {/* Partners Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
<thead className="bg-[#2B4979] text-white">
  <tr>
    <th className="px-4 py-3 text-left">ID</th>
    <th className="px-4 py-3 text-left">Partenaire</th>
    <th className="px-4 py-3 text-left">Email</th>
    <th className="px-4 py-3 text-center">Marge B2B (%)</th>
    <th className="px-4 py-3 text-center">Actions</th>
  </tr>
</thead>
<tbody>
  {partners.map((partner, idx) => (
    <tr 
      key={partner.id_partenaire} 
      className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition ${
        hasChanges(partner.id_partenaire) ? 'border-l-4 border-yellow-400' : ''
      }`}
    >
      <td className="px-4 py-3 font-semibold text-gray-700">{partner.id_partenaire}</td>
      <td className="px-4 py-3">
        <div>
          <p className="font-semibold text-[#2B4979]">{partner.libelle_partenaire}</p>
          <p className="text-sm text-gray-600">{partner.prenom} {partner.nom}</p>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{partner.email}</td>
      
      {/* Marge B2B Input ONLY */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-center">
          <input
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={getDisplayValue(partner, 'marge_b2b')}
            onChange={(e) => handleMargeChange(partner.id_partenaire, 'marge_b2b', e.target.value)}
            className={`w-24 px-3 py-2 border rounded-lg text-center font-semibold focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent ${
              hasChanges(partner.id_partenaire) && changes[partner.id_partenaire].marge_b2b !== undefined
                ? 'border-yellow-400 bg-yellow-50'
                : 'border-gray-300'
            }`}
          />
          <span className="ml-2 text-gray-600 font-semibold">%</span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-2">
          {hasChanges(partner.id_partenaire) && (
            <button
              onClick={() => savePartnerChanges(partner)}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition flex items-center gap-1"
              title="Sauvegarder"
            >
              <Save size={16} />
              Sauvegarder
            </button>
          )}
        </div>
      </td>
    </tr>
  ))}
</tbody>

              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
